import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { translations } from "../translations";
import {
  TYPING_TEST_DURATION,
  buildPassageLines,
  compareTypedChars,
  computeLiveStats,
  computeTypingMetrics,
  computeVisibleLineStart,
  pickRandomTypingPassage,
} from "../utils/typingTest";

const TYPING_LABELS = translations.en;

const TypingTestScreen = ({ typingPassages, onComplete }) => {
  const t = TYPING_LABELS;
  const [passage] = useState(() => pickRandomTypingPassage(typingPassages));
  const [typedText, setTypedText] = useState("");
  const [timeLeft, setTimeLeft] = useState(TYPING_TEST_DURATION);
  const [timerStarted, setTimerStarted] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [finished, setFinished] = useState(false);

  const inputRef = useRef(null);
  const startTimeRef = useRef(null);
  const finishRef = useRef(false);
  const typedTextRef = useRef("");

  const passageLines = useMemo(() => buildPassageLines(passage), [passage]);

  const handleFinish = useCallback(() => {
    if (finishRef.current) return;
    finishRef.current = true;
    setFinished(true);

    const elapsed = startTimeRef.current
      ? Math.max(1, Math.round((Date.now() - startTimeRef.current) / 1000))
      : TYPING_TEST_DURATION;

    const metrics = computeTypingMetrics(passage, typedTextRef.current, elapsed);
    onComplete(metrics);
  }, [onComplete, passage]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!timerStarted || finished) return undefined;

    const intervalId = setInterval(() => {
      if (!startTimeRef.current) return;

      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const remaining = Math.max(0, TYPING_TEST_DURATION - elapsed);
      setElapsedSeconds(elapsed);
      setTimeLeft(remaining);

      if (remaining <= 0) {
        clearInterval(intervalId);
        handleFinish();
      }
    }, 100);

    return () => clearInterval(intervalId);
  }, [timerStarted, finished, handleFinish]);

  const handleInput = (e) => {
    if (finished) return;

    const value = e.target.value;
    if (!timerStarted && value.length > 0) {
      startTimeRef.current = Date.now();
      setTimerStarted(true);
    }

    typedTextRef.current = value;
    setTypedText(value);
  };

  const charStates = useMemo(
    () => compareTypedChars(passage, typedText),
    [passage, typedText]
  );

  const visibleStartLine = useMemo(
    () => computeVisibleLineStart(typedText.length, passageLines),
    [typedText.length, passageLines]
  );

  const liveStats = useMemo(
    () => computeLiveStats(passage, typedText, elapsedSeconds || 1),
    [passage, typedText, elapsedSeconds]
  );

  const renderChar = (item, index) => {
    const isCurrent = index === typedText.length && !finished;
    return (
      <span
        key={index}
        className={`typing-char typing-char-${item.status}${isCurrent ? " typing-char-current" : ""}`}
      >
        {item.char === " " ? "\u00a0" : item.char}
      </span>
    );
  };

  return (
    <div className="page-wrap typing-test-wrap">
      <div className="card typing-test-card">
        <div className="typing-test-header">
          <div>
            <h2 className="typing-test-title">{t.typingTestTitle}</h2>
            <p className="typing-test-instruction">{t.typingTestInstruction}</p>
          </div>
          <div className={`typing-timer-pill ${timerStarted ? "active" : ""}`}>
            <span className="typing-timer-label">
              {timerStarted ? t.secondsLeft : t.startTyping}
            </span>
            <span className="typing-timer-value">{timeLeft}s</span>
          </div>
        </div>

        <div className="typing-live-stats">
          <div className="typing-stat">
            <span className="typing-stat-value">{liveStats.wpm}</span>
            <span className="typing-stat-label">{t.typingWpm}</span>
          </div>
          <div className="typing-stat">
            <span className="typing-stat-value">{liveStats.accuracy}%</span>
            <span className="typing-stat-label">{t.typingAccuracy}</span>
          </div>
          <div className="typing-stat">
            <span className="typing-stat-value">{typedText.length}</span>
            <span className="typing-stat-label">{t.typingChars}</span>
          </div>
        </div>

        <div
          className="typing-passage-wrap"
          onClick={() => inputRef.current?.focus()}
          role="presentation"
        >
          <div className="typing-passage">
            <div className="typing-passage-viewport">
              <div
                className="typing-passage-track"
                style={{
                  transform: `translateY(calc(-${visibleStartLine} * var(--typing-line-height)))`,
                }}
              >
                {passageLines.map((line, lineIdx) => {
                  const lineEnd = passageLines[lineIdx + 1]?.startIndex ?? passage.length;
                  const lineChars = charStates.slice(line.startIndex, lineEnd);

                  return (
                    <div key={lineIdx} className="typing-passage-line">
                      {lineChars.map((item, i) => renderChar(item, line.startIndex + i))}
                      {!finished && typedText.length === passage.length && lineEnd === passage.length && (
                        <span className="typing-cursor-end" aria-hidden="true" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <textarea
            ref={inputRef}
            className="typing-input-overlay"
            value={typedText}
            onChange={handleInput}
            disabled={finished}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-label={t.typingTestTitle}
          />
        </div>
      </div>
    </div>
  );
};

export default TypingTestScreen;
